import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Collapse } from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import { device } from "../../lib/breakpoints";
import { marketingSectionHeading } from "../../styles/marketingSectionTypography";
import { APP_NAME } from "../../helpers/constants";

const { Panel } = Collapse;

/** Home marketing: onboarding, Leo AI, billing, and how we compare. */
const HOME_FAQS = [
  {
    q: "How quickly can I get started with LandlordOps?",
    a: "Most landlords can get started in a few minutes. Add doors, share the tenant Magic Link, and Leo AI handles tenant messages while the full LandlordOps OS runs rent, vendors, ledgers, and tax-ready exports in the same account. Free covers 1-3 units so you can try the flow; upgrade only when you need more doors. Leo feels like texting for tenants. No training required.",
  },
  {
    q: "What makes LandlordOps different from other property management software?",
    a: "LandlordOps is built for self-managing investors and small operators. Every plan includes the full OS: Leo AI behind a tenant Magic Link, automated rent collection, vendor bidding, in-app ledgers, Schedule E-oriented exports, and investor metrics such as portfolio yield and DSCR. Traditional PM firms often charge a large share of rent. LandlordOps is software: you stay in control while Leo handles tenant-facing chat and triage. Tiers differ only by door count (Free 3 · Starter 15 · Growth 50 · Scale 150). You can also use LandlordOps alongside another PM or accounting tool if you prefer.",
  },
  {
    q: "How does billing work when I subscribe?",
    a: "Checkout runs in Stripe. You see the price, renewal terms, and payment method before you pay. Plans are monthly subscriptions with door limits on each tier. Cancel or change plan according to the terms shown at checkout and in your Stripe billing portal.",
  },
  {
    q: "What is Leo AI?",
    a: "Leo AI is the tenant-facing assistant on every plan. Leo handles routine questions, maintenance intake in chat, and emergency escalation using your knowledge base and lease context. The same subscription also includes Stripe rent workflows, in-app ledgers, and vendor tools, including optional auto-approve limits you configure per door for bids. You set strategy and limits. Activity and settings live in the app so you stay in control.",
  },
  {
    q: "How does LandlordOps help with DSCR tracking and refi-readiness?",
    a: "Your dashboard can show debt service coverage (DSCR) and related investor metrics when you add debt service in Settings (or per door) and keep rent and expense data in the product. We highlight when DSCR falls below about 1.2 so you can investigate. When portfolio DSCR is stronger (above about 1.25) and maintenance is in good shape, we may surface lender-oriented portfolio health cues. We do not approve loans or guarantee refi outcomes. Numbers depend on the data you enter and connect.",
  },
  {
    q: "Can LandlordOps help with lease guidance and compliance?",
    a: "Leo AI can summarize and explain common landlord-tenant topics using your uploaded lease and house rules as context. We do not generate leases or legal documents, and nothing in the product is legal advice. For binding documents or advice, consult a qualified attorney.",
  },
  {
    q: "Do you connect to my bank account to auto-import transactions?",
    a: 'We do not offer automatic bank-feed import today. Rent that runs through Stripe is tied to real payments in the app. Expenses are entered manually or via receipt upload so categories stay intentional.\n\n• Income from Stripe: tied to who paid and which door.\n• Expenses: you categorize them so Schedule E-oriented exports reflect what you recorded.\n\nWe prioritize attributable data over guessed bank feeds.',
  },
  {
    q: "How does automated rent collection work?",
    a: "Stripe-based rent collection (reminders, card and ACH payments, late-fee rules, and payment status in your dashboard) is included on every plan. You connect Stripe Connect, set rules, and the product tracks payments against your doors. Card and ACH processing fees depend on your Stripe pricing and how you configure pass-through to tenants. Tenants can still pay outside Stripe. You can log those payments so books stay consistent.",
  },
  {
    q: "What happens with maintenance requests?",
    a: "Tenants can report issues through Leo. Every tier gets triage, structured tickets, vendor bidding, work-order style flows, and optional auto-approve limits for bids. You stay in control of strategy and spend caps.",
  },
  {
    q: "Is my portfolio and tenant data secure?",
    a: "We use encryption in transit (HTTPS), protect stored data with industry-standard practices, and rely on vetted providers for hosting and payments. Card and bank details for rent are processed by Stripe under their PCI program. We do not sell your personal information to data brokers. Some subprocessors process data only to run the service (for example email delivery). See our Privacy Policy for details.",
  },
  {
    q: "What results can I expect from using LandlordOps?",
    a: "Results vary by portfolio size and how you use the product. Our pricing materials reference estimated coordination time saved by tier (for example 5+, 15+, or 30+ hours per month on higher tiers), not a guaranteed outcome. Most users report less back-and-forth with tenants and clearer visibility when they keep Leo and their books current.",
  },
  {
    q: "Can I use LandlordOps on mobile?",
    a: "Yes. The web app works on modern mobile browsers and desktop. You can manage doors and review portfolio health from your phone. Leo keeps handling tenant chat according to your plan.",
  },
  {
    q: "What's included in the pricing?",
    a: "Plans are monthly subscriptions with door limits. No per-door add-on fee on the published tiers. Every plan includes the full LandlordOps OS: Leo AI, rent collection, vendor management, ledgers, Schedule E-oriented exports, and portfolio analytics. Free allows 1-3 units, Starter up to 15 doors, Growth up to 50, and Scale up to 150. Enterprise is quoted for larger portfolios. Cancel according to your subscription terms.",
  },
];

/** /platform page: Rental Command Center workflows, rent, vendors, taxes. */
const FULL_OS_FAQS = [
  {
    q: `What counts as your full ${APP_NAME} Rental Command Center?`,
    a: `Every plan includes the full ${APP_NAME} Rental Command Center: verified rent through Stripe, in-app ledgers and books, maintenance triage with vendor bidding, portfolio analytics (DSCR and yield), Schedule E-oriented exports, Closing Vault-style packets, and Leo AI as your AI First Responder. Free, Starter, Growth, and Scale differ only by how many doors you can manage (3 / 15 / 50 / 150).`,
  },
  {
    q: "Why is rent collection tied to Stripe?",
    a: "We treat rent that runs through Stripe as verified in the ledger: we know who paid, how much, and for which door, so reminders and late-fee logic match reality. You connect Stripe Connect, set rules per portfolio or door, and Leo can assist with reminders and tracking where the product supports it. Tenants can still pay outside the app. You record those payments so books stay complete. Processing fees for cards and ACH depend on your Stripe rates and how you pass fees through.",
  },
  {
    q: "How do vendor bidding and Automated Triage & Dispatch work?",
    a: "Tenants file issues through Leo. We triage urgency on every tier. Leo helps gather details, respects lease context you have uploaded, coordinates vendor bids within limits you set (including optional auto-approve under a dollar cap per door), and keeps a trail from request through bid acceptance. Exact automation depends on your settings and vendor participation.",
  },
  {
    q: "What tax and investor outputs does your Rental Command Center provide?",
    a: "You get categorized expenses, Schedule E oriented exports, and portfolio views that tie rent and NOI-style signals to your doors when the underlying data is in the product. Closing Vault-style packets organize live records for diligence. We do not replace your CPA or prepare tax filings for you. We give attributable data so your accountant or you can file faster.",
  },
  {
    q: "Do I need a higher tier for rent collection and ledgers?",
    a: "No. Automated rent collection, Schedule E tax reporting, vendor management, ledger syncing, and Leo AI ship on Free through Scale. Upgrade only when you need more doors (Free 3 · Starter 15 · Growth 50 · Scale 150).",
  },
  {
    q: "Can I upgrade later when my portfolio grows?",
    a: "Yes. When your Stripe subscription moves to a higher door band, the app raises your unit cap. Your doors, tenants, Leo history, rent ledgers, and vendor workflows stay in one account.",
  },
  {
    q: "Do I need to link my bank for your Rental Command Center to work?",
    a: "No bank feed is required. Rent that flows through Stripe is authoritative for income. Expenses are entered or uploaded so categories stay accurate. By design we avoid guessing from messy bank imports. That keeps Schedule E exports defensible.",
  },
  {
    q: "How does DSCR and portfolio analytics use rent data?",
    a: "With Stripe-linked rent, collected amounts feed the same investor views you use for manual or off-platform payments you log. Debt service from Settings or per door combines with rent and expenses so DSCR and yield reflect performance in the app. The dashboard warns when DSCR falls below about 1.2 and may show lender-oriented cues when DSCR is stronger (about 1.25 or higher), subject to the data you enter.",
  },
  {
    q: "Is tenant and payment data handled securely?",
    a: "Yes. Card and ACH data stay in Stripe's PCI environment. We store references and ledger state in LandlordOps with encryption and access controls consistent with the rest of the product. Tenant chat and the landlord dashboard use HTTPS in transit, like a typical secure web app.",
  },
  {
    q: "Who is your Rental Command Center best suited for?",
    a: "Self-managing investors and small operators who want rent, repairs, and investor reporting in one place, not a separate PM app, rent portal, and spreadsheet. Pick Free, Starter, Growth, or Scale based on door count alone - every plan includes the full OS.",
  },
];

const FAQ_SUBHEADERS = {
  home: `Everything you need to know about ${APP_NAME}`,
  fullOs: `The full ${APP_NAME} OS on every plan - doors are the only difference`,
};

/**
 * @param {{ variant?: 'home' | 'fullOs' }} props
 */
const FAQSection = ({ variant = "home" }) => {
  const [activeKeys, setActiveKeys] = useState([]);

  const faqs = useMemo(
    () => (variant === "fullOs" ? FULL_OS_FAQS : HOME_FAQS),
    [variant]
  );
  const subheader = FAQ_SUBHEADERS[variant] ?? FAQ_SUBHEADERS.home;

  const handleChange = (keys) => {
    setActiveKeys(keys);
  };

  return (
    <Section>
      <Header>Frequently Asked Questions</Header>
      <Subheader>{subheader}</Subheader>
      <AccordionContainer>
        <StyledCollapse
          activeKey={activeKeys}
          onChange={handleChange}
          expandIcon={({ isActive }) =>
            isActive ? <MinusOutlined /> : <PlusOutlined />
          }
          expandIconPosition="end"
        >
          {faqs.map((faq, index) => (
            <Panel header={faq.q} key={`${variant}-${index}`}>
              <AnswerText>{faq.a}</AnswerText>
            </Panel>
          ))}
        </StyledCollapse>
      </AccordionContainer>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
    </Section>
  );
};

export default FAQSection;

const Section = styled.section`
  padding: 5rem 2rem;
  background: #f8fafc;
  text-align: center;
  max-width: 1200px;
  margin: 0 auto;

  @media ${device.mobile} {
    padding: 3rem 1rem;
  }
`;

const Header = styled.h2`
  ${marketingSectionHeading}
  margin-bottom: 0.75rem;
  color: var(--color-text);
`;

const Subheader = styled.p`
  font-size: 1.1rem;
  color: var(--color-text-light);
  margin-bottom: 3rem;
  font-family: var(--font-secondary);

  @media ${device.mobile} {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const AccordionContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: left;
`;

const StyledCollapse = styled(Collapse)`
  background: white;
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  .ant-collapse-item {
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }
  }

  .ant-collapse-header {
    padding: 1.5rem 1.5rem !important;
    font-size: 1.1rem !important;
    font-weight: 600 !important;
    color: var(--color-text) !important;
    font-family: var(--font-secondary) !important;

    &:hover {
      background-color: #fafafa;
    }

    .ant-collapse-expand-icon {
      color: var(--color-primary);
      font-size: 1.1rem;
    }

    @media ${device.mobile} {
      padding: 1.25rem 1rem !important;
      font-size: 1rem !important;
    }
  }

  .ant-collapse-content {
    background: white;
    border-top: 1px solid #f0f0f0;

    .ant-collapse-content-box {
      padding: 0 1.5rem 1.5rem 1.5rem !important;

      @media ${device.mobile} {
        padding: 0 1rem 1.25rem 1rem !important;
      }
    }
  }

  .ant-collapse-item-active {
    .ant-collapse-header {
      background-color: #fafafa;
      border-bottom: 1px solid #f0f0f0;
    }
  }
`;

const AnswerText = styled.div`
  font-size: 1rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  line-height: 1.7;
  padding-top: 0.5rem;
  white-space: pre-line;
`;
